import React, { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import intakeApi from './intakeApi';
import './intake.css';

const ACCEPT = '.xlsx,.xls,.csv,.pdf,.txt,.md,.png,.jpg,.jpeg,.gif,.bmp,.tiff,.webp';

function fileKind(name) {
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tiff', 'webp'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  if (['xlsx', 'xls', 'csv', 'tsv'].includes(ext)) return 'excel';
  return 'text';
}

export default function IntakeUploadPage() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [name, setName] = useState('');
  const [visibility, setVisibility] = useState('private');
  const [dragActive, setDragActive] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const addFiles = useCallback((incoming) => {
    const next = Array.from(incoming || []).filter((f) => f && f.name);
    if (next.length) setFiles((prev) => [...prev, ...next]);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    addFiles(e.dataTransfer?.files);
  }, [addFiles]);

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const onSubmit = async () => {
    if (!files.length) {
      setError('Add at least one file to start a case.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const data = await intakeApi.upload(files, { name: name.trim(), visibility });
      if (!data?.success || !data.caseId) {
        throw new Error(data?.message || 'Upload failed');
      }
      navigate(`/intake/${data.caseId}`);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="intake-page">
      <div className="intake-page__head">
        <div>
          <h1 className="intake-page__title">New denial intake</h1>
          <p className="intake-page__subtitle">
            Upload your starting documents (Excel, PDF, text, or images). We build a
            structured case you can review, complete, and submit.
          </p>
        </div>
        <button type="button" className="intake-btn intake-btn--ghost" onClick={() => navigate('/intake')}>
          Back to queue
        </button>
      </div>

      {error && <div className="intake-banner intake-banner--error">{error}</div>}

      <div
        className={`intake-drop${dragActive ? ' is-active' : ''}`}
        onClick={() => inputRef.current && inputRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
      >
        <div className="intake-drop__hint">
          <strong>Click to choose files</strong> or drag &amp; drop them here
        </div>
        <div className="intake-drop__formats">
          Accepted: Excel (.xlsx/.csv), PDF, text (.txt/.md), images (.png/.jpg)
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          style={{ display: 'none' }}
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
        />
      </div>

      {files.length > 0 && (
        <ul className="intake-filelist">
          {files.map((f, idx) => (
            <li key={`${f.name}-${idx}`} className="intake-filelist__item">
              <span>
                <span className="intake-filelist__kind">{fileKind(f.name)}</span>
                &nbsp;{f.name}
              </span>
              <button type="button" className="intake-filelist__remove" onClick={() => removeFile(idx)}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="intake-form-row">
        <div className="intake-field">
          <label htmlFor="bundle-name">Case label (optional)</label>
          <input
            id="bundle-name"
            type="text"
            value={name}
            placeholder="e.g. Mercy General -- March denials"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="intake-field">
          <label>Queue</label>
          <div className="intake-radio">
            <label>
              <input
                type="radio"
                name="visibility"
                checked={visibility === 'private'}
                onChange={() => setVisibility('private')}
              />
              Private (only me)
            </label>
            <label>
              <input
                type="radio"
                name="visibility"
                checked={visibility === 'global'}
                onChange={() => setVisibility('global')}
              />
              Global (whole team)
            </label>
          </div>
        </div>
      </div>

      <div className="intake-actions">
        <button type="button" className="intake-btn" disabled={busy} onClick={onSubmit}>
          {busy ? 'Building case…' : 'Build case from files'}
        </button>
        {busy && <span className="intake-muted">Extracting text and constructing the CM JSON…</span>}
      </div>
    </div>
  );
}
